import {
  AfterContentChecked,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
} from "@angular/core";
import { AbstractControl, FormControl, FormGroup, Validators } from "@angular/forms";
import { MatOption } from "@angular/material/core/option/option";
import { MatSelectChange } from "@angular/material/select";
import { Router } from "@angular/router";
import * as moment from "moment";
import { ClipboardService } from "ngx-clipboard";
import { Subscription, of } from "rxjs";
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  pairwise,
  startWith,
  switchMap,
  tap,
} from "rxjs/operators";
import { FORMAT } from "../../constants/formats";
import { DeepSet } from "../../types/@extensions/deep-set";
import { compare } from "../../utils/values";
import { NotificationService } from "../notification/notification.service";
import { configuration } from "./dynamic-form-question.configuration";
import { QuestionBase } from "./question-base";
import { DropdownOption, DropdownQuestion } from "./question-dropdown";

@Component({
  selector: "app-question",
  templateUrl: "./dynamic-form-question.component.html",
  styleUrls: ["./dynamic-form-question.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class DynamicFormQuestionComponent implements OnInit, OnDestroy, AfterContentChecked {
  @Input() questions: QuestionBase[];
  @Input() question: QuestionBase<string | boolean>;
  @Input() form: FormGroup;
  @Input() searchControl: FormControl;
  @Output() hasChanged = new EventEmitter<any>();
  inMemoryOptions: DropdownOption[];
  errorMessage: string;
  chipList = [];
  tooltipMessage: string;
  private subscriptions = new Subscription();

  constructor(
    private changeDetector: ChangeDetectorRef,
    private router: Router,
    private clipboardService: ClipboardService,
    private notificationService: NotificationService,
  ) {}

  get control(): AbstractControl {
    return this.form.controls?.[this.question.key];
  }

  ngOnInit(): void {
    if (!this.control) {
      return;
    }

    this.question.questions = this.questions;

    if (this.question.disabled) {
      this.control.disable({ emitEvent: false });
    } else if (this.form.enabled && !this.question.disabled) {
      this.control.enable({ emitEvent: false });
    }
    if (this.question.required) {
      this.applyRequired();
    }
    if (this.question.chipList?.bind) {
      this.bindToChipList();
    }
    if ((this.question as DropdownQuestion).asyncOptions) {
      this.applyAsyncOptions();
    }
    if ((this.question as DropdownQuestion).dependsOn) {
      this.applyDependsOn();
    }
    if ((this.question as DropdownQuestion).search?.filter) {
      this.applySearchFilter();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.hasChanged.unsubscribe();
  }

  ngAfterContentChecked(): void {
    this.changeDetector.detectChanges();
  }

  onChange(event: MatSelectChange): void {
    this.hasChanged.emit(event);
    this.question.callbacks?.onChange?.call(this, event.value);
  }

  dateChange(event: any, key: string): void {
    event.key = key;
    this.hasChanged.emit(event);
    this.question.callbacks?.onChange?.call(this, event.value);
  }

  onFileSelected(event: Event): void {
    const inputNode = event.target as HTMLInputElement;
    const image = inputNode?.files?.[0];
    if (image) {
      this.question.imageReqs.inputNode = inputNode;
      this.question.imageReqs.imageName = image.name;
      this.question.imageReqs.fileTooBig = image.size > this.question.imageReqs.fileRestrictions.maxSize;
      const reader = new FileReader();
      reader.readAsDataURL(image);
      reader.onload = () => {
        const img = new Image();
        img.src = reader.result as string;
        img.onload = () => {
          this.question.imageReqs.invalidDimensions =
            img.naturalWidth > this.question.imageReqs.fileRestrictions.maxWidth ||
            img.naturalHeight > this.question.imageReqs.fileRestrictions.maxHeight;
          this.control.setValue(
            !this.question.imageReqs.fileTooBig && !this.question.imageReqs.invalidDimensions ? img.src : "",
          );
          if (this.control.value) {
            this.control.markAsDirty();
          }
        };
      };
    }
  }

  removeChip(chip: any): void {
    this.chipList = this.chipList.filter((item) => item !== chip);
    this.control.patchValue(this.chipList);
  }

  chipLabel(chip: any): string {
    let label = "";
    this.question.chipList.labelKeys.forEach((lk) => {
      label = lk.format ? label.concat(` - ${lk.format(chip[lk.key])}`) : label.concat(`${chip[lk.key]} `);
    });
    return label;
  }

  redirect(link: string): void {
    if (!link) {
      return;
    }
    this.router.navigate([link]);
  }

  copyToClipboard(question: QuestionBase<string | boolean>) {
    const { key, label } = question;
    const inputValue = this.form.get(key).value;
    if (inputValue) {
      this.clipboardService.copy(inputValue);
      this.notificationService.success(`${label} has been copied!`);
    } else {
      this.notificationService.warning("Empty Field");
    }
  }
  copyDateToClipboard(question: QuestionBase<string | boolean>) {
    const { key, label } = question;
    const inputValue = this.form.get(key).value;
    if (inputValue) {
      const formatDate = moment(inputValue).utc().format(FORMAT.DATE);
      this.clipboardService.copy(formatDate);
      this.notificationService.success(`${label} has been copied!`);
    } else {
      this.notificationService.warning("Empty Field");
    }
  }

  copyDropDownToClipboard($event, question: QuestionBase<string | boolean>, matOption: MatOption) {
    const selectedValue = matOption?.viewValue;
    if ($event) {
      event.stopPropagation();
    }
    if (selectedValue) {
      const inputLabel = question.label;
      this.clipboardService.copy(selectedValue);
      this.notificationService.success(`${inputLabel} has been copied!`);
    } else {
      this.notificationService.warning("Empty Field");
    }
  }

  getInputTooltip = (): string => {
    if (this.question.tooltipEnabled && !this.isEnabled()) {
      return this.question.value as string;
    }
  };

  private applyRequired() {
    this.question.errors.push({
      key: "required",
      validator: Validators.required,
      message: `${this.question.label} is <strong>required</strong>`,
    });
  }

  private applyAsyncOptions() {
    const question = this.question as DropdownQuestion;
    let canAddCurrentOption = true;

    this.control.disable({ emitEvent: false });

    const subscription = question.asyncOptions.subscribe((options) => {
      if (
        canAddCurrentOption &&
        question.currentOption?.label &&
        question.currentOption?.value &&
        !options.find((opt) => opt.value === question.currentOption.value)
      ) {
        options = [question.currentOption, ...options];
      }

      canAddCurrentOption = false;
      if (question.sortOptions) {
        options = options.sort((a, b) => compare(a.label, b.label));
      }
      this.inMemoryOptions = question.options = options;

      if (question.tooltipEnabled) {
        this.setDropdownTooltipMessage();
      }
      if (this.form.enabled && !this.isEnabled() && options.length && !question.disabled) {
        this.control.enable({ emitEvent: false });
      }
    });

    this.subscriptions.add(subscription);
  }

  private async applyDependsOn() {
    const question = this.question as DropdownQuestion;
    const parentControl = this.form.controls[question.dependsOn.key];
    let canAddCurrentOption = true;

    if (!parentControl) {
      return;
    }

    const setQuestionOptions = async (parentValue: string) => {
      if (!question.dependsOn.callback) {
        if (parentValue) {
          this.control.enable({ emitEvent: false });
        } else if (!parentValue) {
          this.control.disable({ emitEvent: false });
        }
      } else if (parentValue && question.dependsOn.callback) {
        question.options = [];
        try {
          const form = this.form;
          let options = await question.dependsOn.callback({ parentValue, form }).toPromise();
          const isFormEnabled = this.form.enabled;
          const isFieldEnabled = this.isEnabled();
          const hasAvailableOptions = !!options.length;
          if (question.sortOptions) {
            options = options.sort((a, b) => compare(a.label, b.label));
          }
          if (
            canAddCurrentOption &&
            question.currentOption?.label &&
            question.currentOption?.value &&
            !options.find((opt) => opt.value === question.currentOption.value)
          ) {
            options = [question.currentOption, ...options];
          }
          canAddCurrentOption = false;
          this.inMemoryOptions = question.options = options;

          if (question.tooltipEnabled) {
            this.setDropdownTooltipMessage();
          }
          if (isFormEnabled && !isFieldEnabled && hasAvailableOptions) {
            this.control.enable({ emitEvent: false });
          } else if (isFormEnabled && isFieldEnabled && !hasAvailableOptions) {
            this.control.disable({ emitEvent: false });
          }
        } catch (err) {}
      }
    };

    if (this.isEnabled() && !question.options?.length) {
      this.control.disable({ emitEvent: false });
    }

    await setQuestionOptions(parentControl.value);

    const subscription = parentControl.valueChanges
      .pipe(startWith(null as string), pairwise())
      .subscribe(async ([prev, next]) => {
        this.control.markAsUntouched();
        if (prev !== next) {
          this.control.setValue("");
        }
        this.control.disable({ emitEvent: false });

        await setQuestionOptions(parentControl.value);
      });

    this.subscriptions.add(subscription);
  }

  private applySearchFilter() {
    const question = this.question as DropdownQuestion;
    const { select } = configuration;

    this.searchControl = new FormControl("");

    const subscription1 = this.searchControl.valueChanges
      .pipe(
        distinctUntilChanged<string>(),
        filter((value) => value?.length === 0),
      )
      .subscribe(() => {
        const selectedValue = this.getOptionWithSelectedValue(this.inMemoryOptions);
        if (selectedValue.length) {
          this.inMemoryOptions = question.options = selectedValue;
        }
        if (question.search.filter.options) {
          this.inMemoryOptions = question.options = question.search.filter.options;
        }
      });

    this.subscriptions.add(subscription1);

    const subscription2 = this.searchControl.valueChanges
      .pipe(
        distinctUntilChanged<string>(),
        tap(() => (question.search.loading = true)),
        debounceTime(select.search.debounceTime),
        map((value) => {
          if (value?.length < select.search.minLength) {
            return of(question.options);
          }

          const parentControl = this.form.controls[question.search.filter.parentKey];
          const parentValue = parentControl?.value;
          const form = this.form;

          return question.search.filter.callback({ parentValue, value, form });
        }),
        switchMap((value) => value),
        tap(() => (question.search.loading = false)),
        finalize(() => (question.search.loading = false)),
      )
      .subscribe((options) => {
        if (!this.searchControl.value) {
          return;
        }
        if (question.sortOptions) {
          options = options.sort((a, b) => compare(a.label, b.label));
        }
        question.options = options;
      });

    this.subscriptions.add(subscription2);
  }

  private getOptionWithSelectedValue(options: DropdownOption[]) {
    const question = this.question as DropdownQuestion;
    const key = this.control.value;
    const selectedValue = key ? question.options.find((opt) => opt.value === key) : undefined;
    return [...new DeepSet([...(options || []), selectedValue])]
      .filter((opt) => opt)
      .sort((a, b) => compare(a.value, b.value));
  }

  private isEnabled() {
    return !this.question.disabled && this.control.enabled;
  }

  private hasError(): boolean {
    const errors = this.question.errors;
    for (const error of errors) {
      const hasError = this.form.hasError(error.key, this.question.key);
      if (hasError) {
        this.errorMessage = error.message;
        return true;
      }
    }
    this.errorMessage = null;
    return false;
  }

  private bindToChipList() {
    this.control.valueChanges.subscribe((items) => {
      if (Array.isArray(items)) {
        const filteredChips = [...this.chipList, ...items].reduce((prev, curr) => {
          const key = this.question.chipList.uniqueKey.map((k) => curr[k]).join("/");
          if (prev.key !== key) {
            return { ...prev, [key]: curr };
          }
        }, {});

        this.chipList = [...Object.values(filteredChips)];
      } else {
        this.chipList = items ? [items] : [];
      }
      this.control.patchValue(this.chipList, { emitEvent: false });
    });
  }

  private setDropdownTooltipMessage(): void {
    const question = this.question as DropdownQuestion;
    if (this.question.controlType === "dropdown") {
      const option = question.options?.find((opt) => {
        return opt.value === question.value;
      });
      this.tooltipMessage = question.currentOption?.label || option?.label;
    }
  }
}
